"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/report-client-error";

/**
 * Global browser-error capture, mounted once in the root layout. Uncaught
 * exceptions and unhandled promise rejections anywhere in the client are
 * reported to /api/client-error (dedupe/budget/noise filters live in
 * lib/report-client-error.ts). React render errors are reported separately by
 * the error boundaries (app/error.tsx, app/global-error.tsx) — this catches
 * everything outside React's render path: event handlers, async work, timers,
 * third-party snippets.
 */
export function ClientErrorReporter() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      const stack = event.error instanceof Error ? event.error.stack : undefined;
      reportClientError("uncaught", event.message, stack, event.filename);
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      const message = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "Unhandled rejection (non-Error reason)";
      const stack = reason instanceof Error ? reason.stack : undefined;
      reportClientError("unhandledrejection", message, stack);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}
